/**
 * Work Timers — Clockify-style work-session tracking.
 *
 * Distinct from the life-event Timers page: this one deals in *named
 * buckets you can start and stop to accrue TimeEntry rows*. Parallel
 * running is intentional — multiple timers may tick at once, and the
 * page polls the server every few seconds so two devices show the same
 * running-state almost immediately.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import PageShell from '../components/PageShell';
import ViewModeToggle from '../components/ViewModeToggle';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import usePersistentViewMode from '../hooks/usePersistentViewMode';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface WorkTimer {
  id: number;
  user_id: number;
  name: string;
  description?: string | null;
  project?: string | null;
  color: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
}

interface TimeEntry {
  id: number;
  user_id: number;
  work_timer_id: number | null;
  description?: string | null;
  project?: string | null;
  tags?: string | null;
  start_time: string;
  end_time?: string | null;
  duration_seconds?: number | null;
  source: string;
}

interface WorkTimerStatus {
  running: TimeEntry[];
  server_time: string;
}

const POLL_INTERVAL_MS = 5000;

const formatDuration = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
};

const WorkTimers: React.FC = () => {
  const {
    cardContentPaddingSx,
    dialogContentPaddingSx,
    pageGridSpacing,
    pageHeaderSx,
    panelPaddingSx,
    sectionGapSx,
  } = useLayoutTokens();
  const [viewMode, setViewMode, viewModeControls] = usePersistentViewMode('work-timers');
  const [timers, setTimers] = useState<WorkTimer[]>([]);
  const [running, setRunning] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0); // force re-render for live durations

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<WorkTimer | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [projectDraft, setProjectDraft] = useState('');
  const [descDraft, setDescDraft] = useState('');
  const [colorDraft, setColorDraft] = useState('#6366F1');
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<WorkTimer | null>(null);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchTimers = useCallback(async () => {
    const response = await axios.get<WorkTimer[]>(`${API_BASE_URL}/api/work-timers/`);
    setTimers(response.data);
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get<WorkTimerStatus>(
        `${API_BASE_URL}/api/work-timers/status`,
      );
      setRunning(response.data.running);
    } catch (err) {
      // Silent — status polls shouldn't spam alerts.
    }
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([fetchTimers(), fetchStatus()]);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load work timers'));
    } finally {
      setLoading(false);
    }
  }, [fetchTimers, fetchStatus]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Cross-device polling for currently-running entries.
  useEffect(() => {
    pollTimer.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [fetchStatus]);

  // 1Hz local tick to render live durations for running entries.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const runningByTimerId = useMemo(() => {
    const map = new Map<number, TimeEntry>();
    for (const entry of running) {
      if (entry.work_timer_id != null) {
        // If multiple exist (parallel), keep the earliest-started.
        const existing = map.get(entry.work_timer_id);
        if (!existing || new Date(entry.start_time) < new Date(existing.start_time)) {
          map.set(entry.work_timer_id, entry);
        }
      }
    }
    return map;
  }, [running]);

  const openCreate = () => {
    setEditTarget(null);
    setNameDraft('');
    setProjectDraft('');
    setDescDraft('');
    setColorDraft('#6366F1');
    setDialogOpen(true);
  };

  const openEdit = (timer: WorkTimer) => {
    setEditTarget(timer);
    setNameDraft(timer.name);
    setProjectDraft(timer.project || '');
    setDescDraft(timer.description || '');
    setColorDraft(timer.color || '#6366F1');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: trimmed,
        project: projectDraft.trim() || null,
        description: descDraft.trim() || null,
        color: colorDraft || '#6366F1',
        is_active: true,
      };
      if (editTarget) {
        await axios.put(`${API_BASE_URL}/api/work-timers/${editTarget.id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/api/work-timers/`, payload);
      }
      setDialogOpen(false);
      await fetchTimers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save work timer'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/work-timers/${deleteTarget.id}`);
      setDeleteTarget(null);
      await Promise.all([fetchTimers(), fetchStatus()]);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete work timer'));
    }
  };

  const handleStart = async (timer: WorkTimer) => {
    try {
      await axios.post(`${API_BASE_URL}/api/work-timers/${timer.id}/start`, {});
      await fetchStatus();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to start timer'));
    }
  };

  const handleStop = async (timer: WorkTimer) => {
    try {
      await axios.post(`${API_BASE_URL}/api/work-timers/${timer.id}/stop`);
      await fetchStatus();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to stop timer'));
    }
  };

  const handleStopAll = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/work-timers/stop-all`);
      await fetchStatus();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to stop all timers'));
    }
  };

  if (loading) {
    return (
      <PageShell>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Box sx={pageHeaderSx}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
        >
          <Box>
            <Typography variant="h3" component="h1">
              Work Timers
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Start, stop, and run multiple work-session timers in parallel.
              Running state is visible across every device where you're signed in.
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignSelf={{ xs: 'stretch', sm: 'center' }}
            justifyContent="flex-end"
            useFlexGap
            flexWrap="wrap"
          >
            <ViewModeToggle
              value={viewMode}
              onChange={setViewMode}
              ariaLabel="work timer view mode"
              isOverride={viewModeControls.isOverride}
              onUseDefault={viewModeControls.useDefault}
            />
            <Tooltip title="Refresh running state">
              <IconButton onClick={fetchStatus} aria-label="Refresh status">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {running.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleStopAll}
              >
                Stop all
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New work timer
            </Button>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip
            label={`${timers.length} timer${timers.length === 1 ? '' : 's'}`}
            size="small"
          />
          <Chip
            label={`${running.length} running`}
            color={running.length > 0 ? 'success' : 'default'}
            size="small"
          />
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {timers.length === 0 ? (
        <Card>
          <CardContent sx={[cardContentPaddingSx, { textAlign: 'center' }]}>
            <Typography variant="h6" gutterBottom>
              No work timers yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first work timer — think "Deep Work", a project name, or a client.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Create your first work timer
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={pageGridSpacing}>
          {timers.map((timer) => {
            const runningEntry = runningByTimerId.get(timer.id);
            const isRunning = Boolean(runningEntry);
            const liveSeconds = runningEntry
              ? Math.max(
                  0,
                  Math.floor((Date.now() - new Date(runningEntry.start_time).getTime()) / 1000),
                )
              : 0;
            // Force dependency on `tick` so the duration re-renders each second.
            void tick;
            return (
              <Grid item xs={12} sm={6} md={4} key={timer.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: `6px solid ${timer.color || '#6366F1'}`,
                  }}
                >
                  <CardContent sx={[cardContentPaddingSx, { flexGrow: 1 }]}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                        {timer.name}
                      </Typography>
                      <Stack direction="row">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(timer)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteTarget(timer)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                    {timer.project && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {timer.project}
                      </Typography>
                    )}
                    {timer.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {timer.description}
                      </Typography>
                    )}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h4" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {isRunning ? formatDuration(liveSeconds) : '00:00:00'}
                      </Typography>
                      <Chip
                        label={isRunning ? 'Running' : 'Idle'}
                        color={isRunning ? 'success' : 'default'}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    {isRunning ? (
                      <Button
                        variant="contained"
                        color="error"
                        fullWidth
                        startIcon={<StopIcon />}
                        onClick={() => handleStop(timer)}
                      >
                        Stop
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleStart(timer)}
                      >
                        Start
                      </Button>
                    )}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Stack spacing={1}>
          {timers.map((timer) => {
            const runningEntry = runningByTimerId.get(timer.id);
            const isRunning = Boolean(runningEntry);
            const liveSeconds = runningEntry
              ? Math.max(
                  0,
                  Math.floor((Date.now() - new Date(runningEntry.start_time).getTime()) / 1000),
                )
              : 0;
            const runningTags = runningEntry?.tags
              ?.split(',')
              .map((tag) => tag.trim())
              .filter(Boolean)
              ?? [];
            // Force dependency on `tick` so the duration re-renders each second.
            void tick;

            return (
              <Paper
                key={timer.id}
                sx={[
                  panelPaddingSx,
                  {
                    borderLeft: `5px solid ${timer.color || '#6366F1'}`,
                  },
                ]}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'minmax(0, 1.2fr) minmax(180px, 0.8fr) minmax(150px, 0.7fr) auto',
                    },
                    gap: { xs: 1, md: 2 },
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                      {timer.name}
                    </Typography>
                    {timer.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {timer.description}
                      </Typography>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {timer.project && <Chip label={timer.project} size="small" variant="outlined" />}
                    {runningTags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                    {!timer.project && runningTags.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No project/tags
                      </Typography>
                    )}
                  </Stack>

                  <Box>
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
                    >
                      {isRunning ? formatDuration(liveSeconds) : '00:00:00'}
                    </Typography>
                    <Chip
                      label={isRunning ? 'Running' : 'Idle'}
                      color={isRunning ? 'success' : 'default'}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>

                  <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    {isRunning ? (
                      <Tooltip title="Stop">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleStop(timer)}
                          aria-label={`Stop ${timer.name}`}
                        >
                          <StopIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Start">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleStart(timer)}
                          aria-label={`Start ${timer.name}`}
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(timer)} aria-label={`Edit ${timer.name}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(timer)}
                        aria-label={`Delete ${timer.name}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => (saving ? undefined : setDialogOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle>{editTarget ? 'Edit work timer' : 'New work timer'}</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            label="Name"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            inputProps={{ maxLength: 255 }}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            fullWidth
            label="Project / Client (optional)"
            value={projectDraft}
            onChange={(e) => setProjectDraft(e.target.value)}
            inputProps={{ maxLength: 255 }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            label="Description (optional)"
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Color"
            type="color"
            value={colorDraft}
            onChange={(e) => setColorDraft(e.target.value)}
            sx={{ width: 120 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !nameDraft.trim()}>
            {saving ? <CircularProgress size={20} /> : editTarget ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete work timer</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          <DialogContentText>
            Delete <strong>{deleteTarget?.name}</strong>? Existing time entries are
            kept — only the timer definition is removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default WorkTimers;
